
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Search, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { createApiUrl } from "@/lib/api";

interface ApiClass {
  _id: string;
  name: string;
  subject: string;
  grade: string;
}

interface Student {
  student_id: string;
  name: string;
  phone: string;
  email: string;
  role: string;
  otp_verified: boolean;
  address: string;
  class_info: any;
}

interface StudentAssignmentDialogProps {
  onStudentAssigned: () => void;
}

const StudentAssignmentDialog = ({ onStudentAssigned }: StudentAssignmentDialogProps) => {
  const [open, setOpen] = useState(false);
  const [classes, setClasses] = useState<ApiClass[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Only fetch data when dialog opens
  useEffect(() => {
    if (open && classes.length === 0) {
      fetchClasses();
    }
    if (open && students.length === 0) {
      fetchStudents();
    }
  }, [open]);

  const fetchClasses = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/teacher/classes'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      }
    } catch (error) {
      console.error('Classes fetch error:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/teacher/students'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filter students that don't have class_info (unassigned students)
        const unassignedStudents = data.students?.filter((student: Student) => !student.class_info) || [];
        setStudents(unassignedStudents);
      }
    } catch (error) {
      console.error('Students fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load student list",
        variant: "destructive"
      });
    }
  };

  const handleAssignStudent = async () => {
    if (!selectedStudent || !selectedClass) {
      toast({
        title: "Error",
        description: "Please select both a student and a class",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl('/teacher/students/assign'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: selectedStudent,
          class_id: selectedClass,
          status: "active",
          joining_date: new Date().toISOString()
        }),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Student assigned to class successfully"
        });
        handleDialogClose();
        onStudentAssigned();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to assign student');
      }
    } catch (error) {
      console.error('Assign student error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to assign student to class",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setSelectedStudent("");
    setSelectedClass("");
    setSearchTerm("");
  };

  // Simplified filtering
  const filteredStudents = students.filter(student => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return student.name.toLowerCase().includes(search) ||
           student.email.toLowerCase().includes(search) ||
           student.phone.includes(searchTerm);
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700">
          <UserPlus className="w-4 h-4 mr-2" />
          Assign Student
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Student to Class</DialogTitle>
          <DialogDescription>
            Select an unassigned student and class to create the assignment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="student-search">Search Students</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                id="student-search"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="student-select">Select Student</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a student" />
              </SelectTrigger>
              <SelectContent className="max-h-[150px]">
                {filteredStudents.length === 0 && (
                  <div className="p-2 text-center text-gray-500 text-sm">
                    {searchTerm ? "No students found" : "No unassigned students"}
                  </div>
                )}
                {filteredStudents.slice(0, 20).map((student) => (
                  <SelectItem key={student.student_id} value={student.student_id}>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <div>
                        <div className="font-medium">{student.name}</div>
                        <div className="text-xs text-gray-500">{student.email}</div>
                      </div>
                      {student.otp_verified && (
                        <Badge variant="outline" className="text-xs ml-2">
                          ✓
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="class-select">Select Class</Label>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a class" />
              </SelectTrigger>
              <SelectContent>
                {classes.map((classItem) => (
                  <SelectItem key={classItem._id} value={classItem._id}>
                    <div>
                      <span className="font-medium">{classItem.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {classItem.grade} • {classItem.subject}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignStudent} 
              disabled={isLoading || !selectedStudent || !selectedClass}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isLoading ? "Assigning..." : "Assign Student"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentAssignmentDialog;
